'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { useRealtime } from '@/contexts/RealtimeContext';
import { usePostBadges } from '@/components/usePostBadges';
import NotificationHub from '@/components/NotificationHub';
import GlobalPeopleSearch from '@/components/GlobalPeopleSearch';

import { HomeIcon, MagnifyingGlassIcon, UserGroupIcon, UserCircleIcon, ExclamationTriangleIcon, AcademicCapIcon, Bars3Icon, XMarkIcon, BellIcon, Cog6ToothIcon, ChevronRightIcon, LockClosedIcon, PencilSquareIcon, ChatBubbleLeftRightIcon, UserPlusIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

// Icons for temporarily hidden sidebar entries:
// import { SparklesIcon, StarIcon, MegaphoneIcon, RectangleStackIcon, PlusCircleIcon, ClipboardDocumentListIcon, SquaresPlusIcon } from '@heroicons/react/24/outline';

// Mobile footer navigation, ordered by the main member journeys.
const bottomTabs = [
  { key: 'tab-chats', label: 'CHATS', description: 'Personal / direct messages', href: '/messages', icon: ChatBubbleLeftRightIcon },
  { key: 'tab-community', label: 'COMMUNITY', description: 'Groups, discussions, networking', href: '/community-groups', icon: UserGroupIcon },
  { key: 'tab-activity', label: 'ACTIVITY', description: 'Posts, updates, engagement', href: '/community-network', icon: GlobeAltIcon },
  { key: 'tab-projects', label: 'PROJECTS', description: 'MLM / Business project discovery', href: '/explore', icon: MagnifyingGlassIcon },
  { key: 'tab-alerts', label: 'ALERTS', description: 'Scam alerts & awareness', href: '/scam-alert-center', icon: ExclamationTriangleIcon },
];

// Full nav items for drawer (without monetization-admin — added conditionally)
// Temporarily hidden entries: uncomment them below to restore sidebar navigation.
const drawerNavItems = [
  // { key: 'nav-home', label: 'Home', href: '/', icon: HomeIcon },
  // { key: 'nav-explore', label: 'Explore', href: '/explore', icon: MagnifyingGlassIcon },
  { key: 'nav-community', label: 'Social', href: '/community-network', icon: GlobeAltIcon },
  { key: 'nav-friends', label: 'Friends', href: '/friends', icon: UserPlusIcon },
  { key: 'nav-community-groups', label: 'Community Groups', href: '/community-groups', icon: UserGroupIcon },
  { key: 'nav-blogs', label: 'Blogs', href: '/blogs', icon: PencilSquareIcon },
  { key: 'nav-messages', label: 'Messages', href: '/messages', icon: ChatBubbleLeftRightIcon },
  { key: 'nav-alerts', label: 'Scam Alerts', href: '/scam-alert-center', icon: ExclamationTriangleIcon, badge: '14' },
  // { key: 'nav-ai-analysis', label: 'AI Analysis', href: '/ai-project-analysis', icon: SparklesIcon },
  { key: 'nav-learning', label: 'Learning Hub', href: '/learning-hub', icon: AcademicCapIcon },
  // { key: 'nav-ratings', label: 'Ratings', href: '/project-ratings', icon: StarIcon },
  // { key: 'nav-submit', label: 'Submit Project', href: '/submit-project', icon: PlusCircleIcon },
  // { key: 'nav-my-submissions', label: 'My Submissions', href: '/my-submissions', icon: ClipboardDocumentListIcon },
  // { key: 'nav-place-ad', label: 'Place an Ad', href: '/place-ad', icon: MegaphoneIcon },
  // { key: 'nav-my-ads', label: 'My Ads', href: '/my-ads', icon: RectangleStackIcon },
  // { key: 'nav-dashboard', label: 'My Dashboard', href: '/dashboard', icon: SquaresPlusIcon },
];

export default function Sidebar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();
  const { user, signOut, profile } = useAuth();
  const { unreadNotifCount, unreadDmCount } = useRealtime();
  const postBadges = usePostBadges(user?.id, pathname);

  // Derive display name and avatar from profile (real-time synced) or fallback to auth metadata
  const displayName = profile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Member';
  const displayUsername = profile?.username ? `@${profile.username}` : `@${user?.email?.split('@')[0] || 'member'}`;
  const avatarUrl = profile?.avatar_url;
  const initials = displayName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();

  const navItems = drawerNavItems;

  const isActive = (href: string) => {
    if (href === '/' && pathname === '/') return true;
    if (href === '/') return false;
    return pathname === href || pathname.startsWith(href + '/');
  };


  return (
    <>
      {/* ─── DESKTOP: Permanent sidebar ─── */}
      <aside className="hidden lg:flex flex-col w-60 h-screen flex-shrink-0 bg-card border-r border-border/60 relative z-20"
        style={{ boxShadow: '2px 0 24px rgba(0,0,0,0.18)' }}
      >
        {/* Logo */}
        <div className="flex items-center gap-2.5 px-4 py-4 border-b border-border/50">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/assets/images/app_logo.png"
            alt="BizzBox Logo"
            className="h-10 w-auto object-contain drop-shadow-md"
          />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-bold text-foreground tracking-tight">BizzBox</span>
            <span className="text-[10px] text-muted-foreground font-medium tracking-wide">Community Network</span>
          </div>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 scrollbar-none relative">
          {/* Guest blur overlay */}
          {!user && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center px-5 rounded-xl"
              style={{ backdropFilter: 'blur(6px)', background: 'rgba(10,16,30,0.55)' }}
            >
              <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center mb-3">
                <LockClosedIcon className="text-primary" style={{ width: 24, height: 24 }} />
              </div>
              <p className="text-sm font-bold text-foreground text-center mb-1">Unlock Full Access</p>
              <p className="text-xs text-muted-foreground text-center mb-4 leading-relaxed">
                Create a free account to explore all features
              </p>
              <Link
                href="/signup"
                className="w-full py-2.5 rounded-xl text-center text-xs font-bold text-white gradient-blue hover:opacity-90 transition-opacity mb-2"
                style={{ boxShadow: '0 2px 12px rgba(59,110,245,0.35)' }}
              >
                Sign Up — It&apos;s Free
              </Link>
              <Link
                href="/login"
                className="w-full py-2.5 rounded-xl text-center text-xs font-semibold text-primary border border-primary/30 hover:bg-primary/10 transition-all"
              >
                Sign In
              </Link>
            </div>
          )}
          <p className="text-muted-foreground/60 text-[9px] font-bold uppercase tracking-[0.12em] px-3 py-2">
            Navigation
          </p>
          <div className="space-y-0.5">
            {navItems.map((item) => {
              const active = isActive(item.href);
              const NavIcon = item.icon;
              const isDmItem = item.href === '/messages';
              const dmBadge = isDmItem && unreadDmCount > 0 ? (unreadDmCount > 99 ? '99+' : String(unreadDmCount)) : null;
              return (
                <Link
                  key={item.key}
                  href={item.href}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-sm font-medium group ${
                    active
                      ? 'text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                  style={active ? {
                    background: 'linear-gradient(135deg, rgba(168,85,247,0.22) 0%, rgba(219,39,119,0.12) 100%)',
                    border: '1px solid rgba(168,85,247,0.28)',
                    boxShadow: '0 2px 12px rgba(168,85,247,0.18)',
                  } : {}}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                    active ? 'bg-primary/20' : 'group-hover:bg-muted'
                  }`}>
                    <NavIcon
                      className={active ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}
                      style={{ width: 17, height: 17 }}
                    />
                  </div>
                  <span className="flex-1">{item.label}</span>
                  {(item.badge || dmBadge) ? (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white shadow-sm"
                      style={{ boxShadow: '0 1px 6px rgba(239,68,68,0.5)' }}
                    >
                      {dmBadge || item.badge}
                    </span>
                  ) : (
                    <ChevronRightIcon style={{ width: 12, height: 12 }} className="text-muted-foreground/30 group-hover:text-muted-foreground/60 transition-colors" />
                  )}
                </Link>
              );
            })}
          </div>

          <div className="mt-4 border-t border-border/50 pt-4 space-y-0.5">
            <p className="text-muted-foreground/60 text-[9px] font-bold uppercase tracking-[0.12em] px-3 py-2">
              Account
            </p>
            <Link
              href="/profile"
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-200 group"
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-muted">
                <UserCircleIcon style={{ width: 17, height: 17 }} className="flex-shrink-0" />
              </div>
              <span className="flex-1">Profile</span>
              <ChevronRightIcon style={{ width: 12, height: 12 }} className="text-muted-foreground/30" />
            </Link>
            {/* Notifications link with live badge */}
            <Link
              href="/notifications"
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group ${
                isActive('/notifications')
                  ? 'text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
              style={isActive('/notifications') ? {
                background: 'linear-gradient(135deg, rgba(168,85,247,0.22) 0%, rgba(219,39,119,0.12) 100%)',
                border: '1px solid rgba(168,85,247,0.28)',
                boxShadow: '0 2px 12px rgba(168,85,247,0.18)',
              } : {}}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                isActive('/notifications') ? 'bg-primary/20' : 'group-hover:bg-muted/80'
              }`}>
                <BellIcon
                  style={{ width: 17, height: 17 }}
                  className={`flex-shrink-0 ${isActive('/notifications') ? 'text-primary' : ''}`}
                />
              </div>
              <span className="flex-1 text-left">Notifications</span>
              {unreadNotifCount > 0 ? (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white shadow-sm"
                  style={{ boxShadow: '0 1px 6px rgba(239,68,68,0.5)' }}
                >
                  {unreadNotifCount > 99 ? '99+' : unreadNotifCount}
                </span>
              ) : (
                <ChevronRightIcon style={{ width: 12, height: 12 }} className="text-muted-foreground/30" />
              )}
            </Link>
            <Link href="/settings" onClick={() => setMobileOpen(false)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-200 group">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-muted/80">
                <Cog6ToothIcon style={{ width: 17, height: 17 }} className="flex-shrink-0" />
              </div>
              <span className="flex-1 text-left">Settings</span>
              <ChevronRightIcon style={{ width: 12, height: 12 }} className="text-muted-foreground/30" />
            </Link>
          </div>
        </nav>

        {/* User profile footer */}
        <div className="border-t border-border/50 p-4">
          <div className="flex items-center gap-3 p-2.5 rounded-xl bg-muted/50">
            <div className="w-9 h-9 rounded-full gradient-brand flex items-center justify-center text-xs font-bold text-white flex-shrink-0 overflow-hidden"
              style={{ boxShadow: '0 2px 12px rgba(168,85,247,0.45)' }}
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt={`${displayName} avatar`} className="w-full h-full object-cover" />
              ) : (
                user ? initials : 'JD'
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">
                {user ? displayName : 'John Doe'}
              </p>
              <p className="text-[11px] text-muted-foreground truncate">
                {user ? displayUsername : '@johndoe · Member since May 2024'}
              </p>
            </div>
          </div>
          {user ? (
            <button
              onClick={signOut}
              className="mt-3 w-full py-2 rounded-xl text-center text-xs font-semibold text-muted-foreground border border-border/50 hover:bg-muted hover:text-foreground transition-all duration-200"
            >
              Sign Out
            </button>
          ) : (
            <div className="mt-3 flex gap-2">
              <Link
                href="/login"
                className="flex-1 py-2 rounded-xl text-center text-xs font-semibold text-primary border border-primary/30 hover:bg-primary/10 transition-all duration-200"
              >
                Sign In
              </Link>
              <Link
                href="/signup"
                className="flex-1 py-2 rounded-xl text-center text-xs font-semibold text-white gradient-blue hover:opacity-90 transition-opacity"
                style={{ boxShadow: '0 2px 12px rgba(59,110,245,0.35)' }}
              >
                Sign Up
              </Link>
            </div>
          )}
        </div>
      </aside>

      {/* ─── MOBILE: Top header bar ─── */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 py-2.5 bg-card/90 backdrop-blur-xl border-b border-border/60"
        style={{ boxShadow: '0 1px 20px rgba(0,0,0,0.3)' }}
      >
        <div className="flex items-center gap-2.5">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/assets/images/app_logo.png"
            alt="BizzBox Logo"
            className="h-10 w-auto object-contain drop-shadow-md"
          />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-bold text-foreground tracking-tight">BizzBox</span>
            <span className="text-[10px] text-muted-foreground font-medium tracking-wide">Community Network</span>
          </div>
        </div>
        <div className="flex items-center gap-0.5">
          <GlobalPeopleSearch />
          <Link
            href={user ? '/profile' : '/login'}
            className="p-1.5 rounded-xl hover:bg-muted transition-all duration-200"
            aria-label={user ? 'View your profile' : 'Sign in'}
          >
            <span className="w-8 h-8 rounded-full gradient-brand flex items-center justify-center text-xs font-bold text-white overflow-hidden">
              {user && avatarUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
              ) : user ? initials : (
                <UserCircleIcon className="w-6 h-6" />
              )}
            </span>
          </Link>
          <NotificationHub />
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground transition-all duration-200"
            aria-label="Open menu"
          >
            <Bars3Icon style={{ width: 21, height: 21 }} />
          </button>
        </div>
      </header>

      {/* ─── MOBILE: Drawer overlay ─── */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 flex"
          onClick={() => setMobileOpen(false)}
        >
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />
          <div
            className="relative w-72 max-w-[85vw] h-full flex flex-col z-10 animate-slide-in-right bg-card border-r border-border"
            style={{
              boxShadow: '4px 0 40px rgba(0,0,0,0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between px-4 py-4 border-b border-border/50">
              <div className="flex items-center gap-2.5">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/assets/images/app_logo.png"
                  alt="BizzBox Logo"
                  className="h-11 w-auto object-contain drop-shadow-md"
                />
                <div className="flex flex-col leading-tight">
                  <span className="text-sm font-bold text-foreground tracking-tight">BizzBox</span>
                  <span className="text-[10px] text-muted-foreground font-medium tracking-wide">Community Network</span>
                </div>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-1.5 rounded-xl hover:bg-white/8 text-muted-foreground hover:text-foreground transition-all duration-200"
              >
                <XMarkIcon style={{ width: 20, height: 20 }} />
              </button>
            </div>

            {/* Nav items */}
            <nav className={`flex-1 py-3 px-2 scrollbar-none relative ${user ? 'overflow-y-auto' : 'overflow-y-hidden'}`}>
              {/* Guest blur overlay */}
              {!user && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center px-5 rounded-xl"
                  style={{ backdropFilter: 'blur(6px)', background: 'rgba(10,16,30,0.55)' }}
                >
                  <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center mb-3">
                    <LockClosedIcon className="text-primary" style={{ width: 24, height: 24 }} />
                  </div>
                  <p className="text-sm font-bold text-foreground text-center mb-1">Unlock Full Access</p>
                  <p className="text-xs text-muted-foreground text-center mb-4 leading-relaxed">
                    Create a free account to explore all features
                  </p>
                  <Link
                    href="/signup"
                    onClick={() => setMobileOpen(false)}
                    className="w-full py-2.5 rounded-xl text-center text-xs font-bold text-white gradient-blue hover:opacity-90 transition-opacity mb-2"
                    style={{ boxShadow: '0 2px 12px rgba(59,110,245,0.35)' }}
                  >
                    Sign Up — It&apos;s Free
                  </Link>
                  <Link
                    href="/login"
                    onClick={() => setMobileOpen(false)}
                    className="w-full py-2.5 rounded-xl text-center text-xs font-semibold text-primary border border-primary/30 hover:bg-primary/10 transition-all"
                  >
                    Sign In
                  </Link>
                </div>
              )}
              <p className="text-muted-foreground/60 text-[9px] font-bold uppercase tracking-[0.12em] px-3 py-2">
                Navigation
              </p>
              <div className="space-y-0.5">
                {navItems.map((item) => {
                  const active = isActive(item.href);
                  const NavIcon = item.icon;
                  const isDmItem = item.href === '/messages';
                  const dmBadge = isDmItem && unreadDmCount > 0 ? (unreadDmCount > 99 ? '99+' : String(unreadDmCount)) : null;
                  return (
                    <Link
                      key={item.key}
                      href={item.href}
                      onClick={() => setMobileOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-sm font-medium group ${
                        active
                          ? 'text-foreground' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                      style={active ? {
                        background: 'linear-gradient(135deg, rgba(168,85,247,0.22) 0%, rgba(219,39,119,0.12) 100%)',
                        border: '1px solid rgba(168,85,247,0.28)',
                        boxShadow: '0 2px 12px rgba(168,85,247,0.18)',
                      } : {}}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                        active ? 'bg-primary/20' : 'group-hover:bg-muted'
                      }`}>
                        <NavIcon
                          className={active ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}
                          style={{ width: 17, height: 17 }}
                        />
                      </div>
                      <span className="flex-1">{item.label}</span>
                      {(item.badge || dmBadge) ? (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white shadow-sm"
                          style={{ boxShadow: '0 1px 6px rgba(239,68,68,0.5)' }}
                        >
                          {dmBadge || item.badge}
                        </span>
                      ) : (
                        <ChevronRightIcon style={{ width: 12, height: 12 }} className="text-muted-foreground/30 group-hover:text-muted-foreground/60 transition-colors" />
                      )}
                    </Link>
                  );
                })}
              </div>

              <div className="mt-4 border-t border-border/50 pt-4 space-y-0.5">
                <p className="text-muted-foreground/60 text-[9px] font-bold uppercase tracking-[0.12em] px-3 py-2">
                  Account
                </p>
                <Link
                  href="/profile"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-200 group"
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-muted">
                    <UserCircleIcon style={{ width: 17, height: 17 }} className="flex-shrink-0" />
                  </div>
                  <span className="flex-1">Profile</span>
                  <ChevronRightIcon style={{ width: 12, height: 12 }} className="text-muted-foreground/30" />
                </Link>
                {/* Notifications link with live badge */}
                <Link
                  href="/notifications"
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group ${
                    isActive('/notifications')
                      ? 'text-foreground' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                  style={isActive('/notifications') ? {
                    background: 'linear-gradient(135deg, rgba(168,85,247,0.22) 0%, rgba(219,39,119,0.12) 100%)',
                    border: '1px solid rgba(168,85,247,0.28)',
                    boxShadow: '0 2px 12px rgba(168,85,247,0.18)',
                  } : {}}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                    isActive('/notifications') ? 'bg-primary/20' : 'group-hover:bg-muted/80'
                  }`}>
                    <BellIcon
                      style={{ width: 17, height: 17 }}
                      className={`flex-shrink-0 ${isActive('/notifications') ? 'text-primary' : ''}`}
                    />
                  </div>
                  <span className="flex-1 text-left">Notifications</span>
                  {unreadNotifCount > 0 ? (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white shadow-sm"
                      style={{ boxShadow: '0 1px 6px rgba(239,68,68,0.5)' }}
                    >
                      {unreadNotifCount > 99 ? '99+' : unreadNotifCount}
                    </span>
                  ) : (
                    <ChevronRightIcon style={{ width: 12, height: 12 }} className="text-muted-foreground/30" />
                  )}
                </Link>
                <Link href="/settings" onClick={() => setMobileOpen(false)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-200 group">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-muted/80">
                    <Cog6ToothIcon style={{ width: 17, height: 17 }} className="flex-shrink-0" />
                  </div>
                  <span className="flex-1 text-left">Settings</span>
                  <ChevronRightIcon style={{ width: 12, height: 12 }} className="text-muted-foreground/30" />
                </Link>
              </div>
            </nav>

            {/* User profile */}
            <div className="border-t border-border/50 p-4">
              <div className="flex items-center gap-3 p-2.5 rounded-xl bg-muted/50">
                <div className="w-9 h-9 rounded-full gradient-brand flex items-center justify-center text-xs font-bold text-white flex-shrink-0 overflow-hidden"
                  style={{ boxShadow: '0 2px 12px rgba(168,85,247,0.45)' }}
                >
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={`${displayName} avatar`} className="w-full h-full object-cover" />
                  ) : (
                    user ? initials : 'JD'
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">
                    {user ? displayName : 'John Doe'}
                  </p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {user ? displayUsername : '@johndoe · Member since May 2024'}
                  </p>
                </div>
              </div>
              {!user && (
                <div className="mt-3 flex gap-2">
                  <Link
                    href="/login"
                    className="flex-1 py-2 rounded-xl text-center text-xs font-semibold text-primary border border-primary/30 hover:bg-primary/10 transition-all duration-200"
                  >
                    Sign In
                  </Link>
                  <Link
                    href="/signup"
                    className="flex-1 py-2 rounded-xl text-center text-xs font-semibold text-white gradient-blue hover:opacity-90 transition-opacity"
                    style={{ boxShadow: '0 2px 12px rgba(59,110,245,0.35)' }}
                  >
                    Sign Up
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── MOBILE: Bottom navigation bar ─── */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-card/90 backdrop-blur-xl border-t border-border/60 flex items-center justify-around px-1 py-1.5"
        style={{ boxShadow: '0 -4px 24px rgba(0,0,0,0.3)' }}
      >
        {bottomTabs.map((tab) => {
          const active = isActive(tab.href);
          const TabIcon = tab.icon;
          const count = tab.href === '/messages' ? unreadDmCount
            : tab.href === '/community-groups' ? postBadges.community
            : tab.href === '/community-network' ? postBadges.activity : 0;
          const badge = count > 0 ? (count > 99 ? '99+' : String(count)) : null;

          return (
            <Link
              key={tab.key}
              href={tab.href}
              title={tab.description}
              aria-label={`${tab.label}: ${tab.description}${count > 0 ? `, ${count} ${tab.href === '/messages' ? 'unread messages' : 'unseen posts'}` : ''}`}
              aria-current={active ? 'page' : undefined}
              className={`relative flex flex-col items-center gap-0.5 px-1 py-1.5 rounded-xl transition-all duration-200 min-w-0 flex-1 ${
                active ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {active && (
                <span className="absolute inset-0 rounded-xl" style={{
                  background: 'linear-gradient(135deg, rgba(59,110,245,0.18) 0%, rgba(59,110,245,0.06) 100%)',
                }} />
              )}
              <div className="relative">
                <TabIcon style={{ width: 22, height: 22 }} />
                {badge && (
                  <span className="absolute -top-1.5 -right-1.5 text-[8px] font-bold min-w-4 px-1 h-4 flex items-center justify-center rounded-full bg-red-500 text-white"
                    style={{ boxShadow: '0 1px 6px rgba(239,68,68,0.5)' }}
                  >
                    {badge}
                  </span>
                )}
              </div>
              <span className={`text-[10px] font-semibold truncate ${active ? 'text-primary' : ''}`}>
                {tab.label}
              </span>
            </Link>
          );
        })}
      </nav>
    </>
  );
}
